// ============================================================================
// useSttAvailability — ЕДИНАЯ проверка доступности голоса (SSoT/SRP).
//
// Один источник для ВСЕХ мест, где есть голос:
//   • чат (запись голосового — только микрофон);
//   • голосовой ввод в полях (микрофон + распознавание речи);
//   • голосовой поиск в списках и глобальном поиске «Пуск».
//
// Правила (консультация §2):
//   запись голосового      = только микрофон (MediaRecorder/getUserMedia);
//   голос в текст (STT)    = микрофон И SpeechRecognition;
//   denied                 = кнопки НЕТ;
//   prompt                 = кнопка есть: системный запрос делает жест
//                            пользователя (startStt/getUserMedia по клику).
//
// Состояние разрешения берётся из permissionCenter (единый центр разрешений),
// НЕ из локальных проверок компонентов.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { getPermissionState, requestPermission, type PermissionState } from '@/lib/permissionCenter';
import { sttAvailable } from '@/lib/stt';
import { isWailsMode } from '@/lib/wailsBridge';
import { logSwallowed } from '@/lib/errors';

/** Разрешение микрофона в терминах голосового интерфейса. */
export type SttPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

export type SttPlatform = 'desktop' | 'mobile' | 'web';

export interface SttAvailability {
  /** Можно ли записать голосовое (есть микрофон, разрешение не запрещено). */
  canRecordVoice: boolean;
  /** Можно ли распознать речь в текст (запись И SpeechRecognition). */
  canTranscribe: boolean;
  /** Состояние разрешения микрофона. */
  permission: SttPermission;
  /** Среда выполнения (для различий интерфейса/подсказок). */
  platform: SttPlatform;
  /** Запросить доступ к микрофону (вызывать ТОЛЬКО из жеста пользователя). */
  requestPermission: () => Promise<SttPermission>;
}

export interface SttAvailabilityInput {
  /** navigator.mediaDevices.getUserMedia доступен. */
  hasMicrophoneApi: boolean;
  /** SpeechRecognition/webkitSpeechRecognition доступен. */
  hasRecognition: boolean;
  /** Состояние из Permissions API (permissionCenter). */
  permission: PermissionState;
}

export interface SttAvailabilityResolution {
  canRecordVoice: boolean;
  canTranscribe: boolean;
  permission: SttPermission;
}

function mapPermission(state: PermissionState): SttPermission {
  if (state === 'granted') return 'granted';
  if (state === 'denied') return 'denied';
  if (state === 'default') return 'prompt';
  return 'unknown';
}

/**
 * Чистое правило доступности (без React/DOM) — тестируется напрямую.
 * denied отключает и запись, и распознавание (кнопок нет).
 */
export function resolveSttAvailability(input: SttAvailabilityInput): SttAvailabilityResolution {
  const permission = mapPermission(input.permission);
  if (permission === 'denied') {
    return { canRecordVoice: false, canTranscribe: false, permission };
  }
  const canRecordVoice = input.hasMicrophoneApi;
  return {
    canRecordVoice,
    canTranscribe: canRecordVoice && input.hasRecognition,
    permission,
  };
}

/** Среда выполнения: desktop (Wails), mobile (Capacitor/узкий экран), web. */
export function detectVoicePlatform(): SttPlatform {
  try {
    if (isWailsMode()) return 'desktop';
  } catch (e) {
    logSwallowed(e, 'lib/useSttAvailability.ts:platform:wails');
  }
  try {
    if (Capacitor.isNativePlatform()) return 'mobile';
  } catch (e) {
    logSwallowed(e, 'lib/useSttAvailability.ts:platform:capacitor');
  }
  if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches) {
    return 'mobile';
  }
  return 'web';
}

function hasMicrophoneApi(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/**
 * Хук доступности голоса. Один на все места (чат/поля/поиск).
 * Permission API может не поддержать 'microphone' (WebView2) — тогда
 * 'unknown', но кнопки остаются: запрос произойдёт по жесту пользователя.
 */
export function useSttAvailability(): SttAvailability {
  const [permissionState, setPermissionState] = useState<PermissionState>('default');

  useEffect(() => {
    let alive = true;
    void getPermissionState('microphone')
      .then((state) => { if (alive) setPermissionState(state); })
      .catch((e) => logSwallowed(e, 'lib/useSttAvailability.ts:query'));

    // Живое обновление, если браузер поддерживает события PermissionStatus.
    let status: PermissionStatus | null = null;
    const onChange = () => {
      if (status) setPermissionState(status.state as PermissionState);
    };
    if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
      navigator.permissions.query({ name: 'microphone' as PermissionName })
        .then((s) => {
          if (!alive) return;
          status = s;
          setPermissionState(s.state as PermissionState);
          s.addEventListener?.('change', onChange);
        })
        .catch((e) => logSwallowed(e, 'lib/useSttAvailability.ts:subscribe'));
    }
    return () => {
      alive = false;
      try { status?.removeEventListener?.('change', onChange); } catch (e) {
        logSwallowed(e, 'lib/useSttAvailability.ts:unsubscribe');
      }
    };
  }, []);

  const platform = useMemo(() => detectVoicePlatform(), []);
  const resolution = resolveSttAvailability({
    hasMicrophoneApi: hasMicrophoneApi(),
    hasRecognition: sttAvailable(),
    permission: permissionState,
  });

  const ask = useCallback(async (): Promise<SttPermission> => {
    const next = await requestPermission('microphone');
    setPermissionState(next);
    return mapPermission(next);
  }, []);

  return { ...resolution, platform, requestPermission: ask };
}
